/**
 * Phone: intl-tel-input (libphonenumber)
 *
 * @package DesignSchool\Forms
 */

import intlTelInput from 'intl-tel-input';
import ru from 'intl-tel-input/i18n/ru';
import { buildPhonePlaceholder, type PhoneCountryData } from './phone-placeholder';
import { intlUtilsReady, isIntlUtilsLoaded } from './phone-utils-bootstrap';

declare var jQuery: any;

export const PHONE_INPUT_SELECTOR =
    'input.ds-form__input--phone[type="tel"], input[type="tel"].phone[name], #form2_popup input[type="tel"][name]';

export const PHONE_SCOPE_SELECTOR =
    '.ds-form, .ds-modal, #form2_popup, .ds-forms-preloaded';

type ItiInstance = NonNullable<ReturnType<typeof intlTelInput.getInstance>>;

type PhoneInputHandlers = {
    onInput: () => void;
    onFocus: () => void;
    onBlur: () => void;
    onCountryChange: () => void;
    onKeydown: (e: KeyboardEvent) => void;
};

export class PhoneMask {
    private $: any;
    private initialized: boolean = false;

    constructor() {
        this.$ = jQuery;
    }

    public init(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        if (!isIntlUtilsLoaded()) {
            console.error('[PhoneMask] libphonenumber utils not loaded');
            return;
        }

        this.applyMasksInScope();
        this.watchForNewFields();
        this.$(document).on('ds-forms-modal-closed', () => {
            this.destroyInContainer(this.$('.ds-modal'));
        });
    }

    public applyMasksInScope($root?: any): void {
        if (!isIntlUtilsLoaded()) {
            return;
        }

        const $scope = $root && $root.length ? $root : this.$(PHONE_SCOPE_SELECTOR);
        if (!$scope.length) {
            return;
        }

        $scope.find(PHONE_INPUT_SELECTOR).each((_: number, el: HTMLElement) => {
            if (el instanceof HTMLInputElement) {
                this.initOnInput(el);
            }
        });
    }

    public destroyInContainer($root: any): void {
        if (!$root || !$root.length) {
            return;
        }
        $root.find(PHONE_INPUT_SELECTOR).each((_: number, el: HTMLElement) => {
            if (el instanceof HTMLInputElement) {
                this.destroyOnInput(el);
            }
        });
    }

    private destroyOnInput(el: HTMLInputElement): void {
        const handlers = (el as HTMLInputElement & { _dsPhoneHandlers?: PhoneInputHandlers })._dsPhoneHandlers;
        if (handlers) {
            el.removeEventListener('input', handlers.onInput);
            el.removeEventListener('focus', handlers.onFocus);
            el.removeEventListener('blur', handlers.onBlur);
            el.removeEventListener('countrychange', handlers.onCountryChange);
            el.removeEventListener('keydown', handlers.onKeydown);
            delete (el as HTMLInputElement & { _dsPhoneHandlers?: PhoneInputHandlers })._dsPhoneHandlers;
        }

        const inst = intlTelInput.getInstance(el);
        if (inst) {
            inst.destroy();
        }
        this.stripLegacyMasks(el);
    }

    private stripLegacyMasks(el: HTMLInputElement): void {
        const $el = this.$(el);
        if (typeof this.$.fn.inputmask !== 'undefined') {
            try {
                if ($el.data('inputmask')) {
                    $el.inputmask('remove');
                }
            } catch {
                // ignore
            }
        }
        if (typeof this.$.fn.inputmasks === 'function') {
            try {
                if ($el.data('inputmasks')) {
                    $el.inputmasks('remove');
                }
            } catch {
                // ignore
            }
        }
    }

    private unwrapOrphanIti(el: HTMLInputElement): HTMLInputElement {
        const wrapper = el.closest('.iti');
        if (!wrapper || intlTelInput.getInstance(el)) {
            return el;
        }
        const parent = wrapper.parentNode;
        if (parent) {
            parent.insertBefore(el, wrapper);
            wrapper.remove();
        }
        return el;
    }

    private shouldInitElement(el: HTMLInputElement): boolean {
        if (el.closest('.ds-forms-preloaded') && el.offsetParent === null) {
            return false;
        }
        return true;
    }

    private buildOptions(): Record<string, unknown> {
        return {
            initialCountry: 'ru',
            nationalMode: false,
            separateDialCode: false,
            formatAsYouType: true,
            formatOnDisplay: true,
            strictMode: true,
            showFlags: true,
            countrySearch: true,
            allowDropdown: true,
            autoPlaceholder: 'aggressive',
            customPlaceholder: (exampleNumber: string, selectedCountryData: object) =>
                buildPhonePlaceholder(exampleNumber, selectedCountryData as PhoneCountryData),
            preferredCountries: ['ru', 'by', 'kz', 'ua', 'us', 'gb', 'de', 'fr', 'it', 'es'],
            i18n: ru,
        };
    }

    private initOnInput(el: HTMLInputElement): void {
        void this.setupInput(el);
    }

    private async setupInput(el: HTMLInputElement): Promise<void> {
        if (!el || el.tagName !== 'INPUT' || !el.name) {
            return;
        }

        await intlUtilsReady;

        if (!this.shouldInitElement(el)) {
            return;
        }

        el = this.unwrapOrphanIti(el);
        this.stripLegacyMasks(el);

        if (intlTelInput.getInstance(el)) {
            return;
        }

        const iti = intlTelInput(el, this.buildOptions());
        this.bindPhoneHelpers(el, iti);
        this.clearValueForPlaceholder(el);
        this.syncPlaceholder(el, iti);
    }

    private bindPhoneHelpers(el: HTMLInputElement, iti: ItiInstance): void {
        const onInput = () => {
            this.ensureLeadingPlus(el, iti);
            this.normalizeRuEightTrunk(el, iti);
        };
        const onFocus = () => this.ensureLeadingPlus(el, iti);
        const onBlur = () => this.clearValueForPlaceholder(el);
        const onCountryChange = () => this.syncPlaceholder(el, iti);
        const onKeydown = (e: KeyboardEvent) => this.guardPlusPrefix(el, e);

        const handlers: PhoneInputHandlers = { onInput, onFocus, onBlur, onCountryChange, onKeydown };
        (el as HTMLInputElement & { _dsPhoneHandlers?: PhoneInputHandlers })._dsPhoneHandlers = handlers;

        el.addEventListener('input', onInput);
        el.addEventListener('focus', onFocus);
        el.addEventListener('blur', onBlur);
        el.addEventListener('countrychange', onCountryChange);
        el.addEventListener('keydown', onKeydown);
    }

    /** пустое value - виден placeholder ITI (+7 (___) ...) */
    private clearValueForPlaceholder(el: HTMLInputElement): void {
        const trimmed = (el.value || '').trim();
        if (trimmed === '' || trimmed === '+') {
            el.value = '';
        }
    }

    private syncPlaceholder(el: HTMLInputElement, iti: ItiInstance): void {
        const data = iti.getSelectedCountryData();
        if (!data?.iso2) {
            return;
        }

        const utils = (intlTelInput as typeof intlTelInput & { utils?: { getExampleNumber: (iso2: string, national: boolean, type: number) => string; numberType: { MOBILE: number } } }).utils;
        if (!utils) {
            return;
        }

        const example = utils.getExampleNumber(data.iso2, false, utils.numberType.MOBILE) || '';
        const placeholder = buildPhonePlaceholder(example, data as PhoneCountryData);
        if (placeholder) {
            el.setAttribute('placeholder', placeholder);
        }
    }

    /** при вводе: "+" по умолчанию, без кода страны в value */
    private ensureLeadingPlus(el: HTMLInputElement, iti: ItiInstance): void {
        if (el.dataset.dsPhoneNormalizing === '1') {
            return;
        }

        const trimmed = (el.value || '').trim();

        if (trimmed === '') {
            if (document.activeElement === el) {
                el.dataset.dsPhoneNormalizing = '1';
                try {
                    el.value = '+';
                    el.setSelectionRange(1, 1);
                } finally {
                    delete el.dataset.dsPhoneNormalizing;
                }
            }
            return;
        }

        if (!trimmed.startsWith('+')) {
            el.dataset.dsPhoneNormalizing = '1';
            try {
                const digits = trimmed.replace(/\D/g, '');
                iti.setNumber('+' + digits);
            } finally {
                delete el.dataset.dsPhoneNormalizing;
            }
        }
    }

    private guardPlusPrefix(el: HTMLInputElement, e: KeyboardEvent): void {
        if (e.key !== 'Backspace' && e.key !== 'Delete') {
            return;
        }
        const value = el.value || '';
        if (!value.startsWith('+')) {
            return;
        }

        const selectionStart = el.selectionStart ?? 0;
        const selectionEnd = el.selectionEnd ?? selectionStart;
        const hasSelection = selectionEnd > selectionStart;
        const selectionCoversOnlyPlus = selectionStart <= 1 && selectionEnd <= 1;

        // разрешаем удаление, если выделено больше, чем только префикс "+"
        if (hasSelection && !selectionCoversOnlyPlus) {
            return;
        }

        if (selectionStart <= 1) {
            e.preventDefault();
        }
    }

    /**
     * 8/89/8916... -> +7... (не трогаем +81, +86 и т.п.)
     */
    private normalizeRuEightTrunk(el: HTMLInputElement, iti: ItiInstance): void {
        if (el.dataset.dsPhoneNormalizing === '1') {
            return;
        }

        const raw = (el.value || '').trim();
        if (!raw) {
            return;
        }

        const digits = raw.replace(/\D/g, '');
        if (!digits.startsWith('8')) {
            return;
        }

        if (this.isInternationalDialInProgress(digits)) {
            return;
        }

        const shouldConvert =
            digits.startsWith('89') && digits.length >= 2 ||
            (!raw.startsWith('+') && digits.startsWith('8') && digits.length >= 2) ||
            digits.length >= 11;

        if (!shouldConvert) {
            return;
        }

        el.dataset.dsPhoneNormalizing = '1';
        try {
            iti.setNumber('+7' + digits.slice(1));
        } finally {
            delete el.dataset.dsPhoneNormalizing;
        }
    }

    /** +81, +86... - пока набирают код страны, 8 не заменяем на 7 */
    private isInternationalDialInProgress(digits: string): boolean {
        const prefixes = ['81', '82', '84', '86', '850', '852', '853', '855', '856', '880'];
        for (const prefix of prefixes) {
            if (digits.startsWith(prefix) && digits.length <= prefix.length) {
                return true;
            }
        }
        return false;
    }

    public getTelValue(el: HTMLInputElement): string {
        const raw = (el.value || '').trim();
        const iti = intlTelInput.getInstance(el);
        if (!iti) {
            return raw === '+' ? '' : raw;
        }

        try {
            const num = iti.getNumber();
            if (num) {
                return num;
            }
        } catch {
            // ignore
        }

        if (raw === '+' || raw === '') {
            return '';
        }

        const fallback = this.buildE164Fallback(el, iti);
        if (fallback) {
            return fallback;
        }

        return raw;
    }

    private buildE164Fallback(
        el: HTMLInputElement,
        iti: NonNullable<ReturnType<typeof intlTelInput.getInstance>>
    ): string {
        const raw = (el.value || '').trim();
        if (!raw || raw === '+') {
            return '';
        }
        const digits = raw.replace(/\D/g, '');
        if (!digits) {
            return '';
        }
        if (raw.startsWith('+')) {
            return '+' + digits;
        }

        const data = iti.getSelectedCountryData();
        const dialCode = data && data.dialCode ? String(data.dialCode) : '';
        const iso2 = data && data.iso2 ? String(data.iso2).toLowerCase() : '';

        if (!dialCode) {
            return digits;
        }

        if ((iso2 === 'us' || iso2 === 'ca') && digits.length === 10) {
            return '+' + dialCode + digits;
        }

        if (digits.startsWith('8') && digits.length >= 11) {
            return '+7' + digits.slice(1);
        }

        if (digits.startsWith(dialCode)) {
            return '+' + digits;
        }

        if (digits.length >= 10) {
            return '+' + dialCode + digits;
        }

        return raw;
    }

    public getTelDigitCount(el: HTMLInputElement): number {
        const fromValue = (el.value || '').replace(/\D/g, '').length;
        const fromApi = this.getTelValue(el).replace(/\D/g, '').length;
        return Math.max(fromValue, fromApi);
    }

    public applyPhonesToFormData(formData: FormData, $form: any): void {
        const $f = this.$($form);
        $f.find(PHONE_INPUT_SELECTOR).each((_: number, el: HTMLElement) => {
            if (!(el instanceof HTMLInputElement) || !el.name) {
                return;
            }
            const normalized = this.getTelValue(el);
            if (normalized) {
                formData.set(el.name, normalized);
            }
        });
    }

    public syncTelInputsToE164($form: any): void {
        // оставлено для совместимости
    }

    private watchForNewFields(): void {
        this.$(document).on('form-loaded', () => {
            const $modal = this.$('.ds-modal');
            const run = () => {
                this.stripLegacyMasksInScope($modal);
                this.applyMasksInScope($modal);
            };
            run();
            window.setTimeout(run, 50);
            window.setTimeout(run, 300);
        });
    }

    private stripLegacyMasksInScope($root: any): void {
        if (!$root || !$root.length) {
            return;
        }
        $root.find(PHONE_INPUT_SELECTOR).each((_: number, el: HTMLElement) => {
            if (el instanceof HTMLInputElement) {
                this.stripLegacyMasks(el);
            }
        });
    }

    public initElement(selector: string): void {
        const el = this.$(selector)[0] as HTMLInputElement | undefined;
        if (!el) {
            return;
        }
        this.initOnInput(el);
    }
}
